import { CustomSelect, SelectOption } from '@consigli/facade';
import { useProjectId, useServiceName, useLazyGetPackagesByTypeQuery } from '@consigli/hooks';
import { DocumentPackageType, ServiceName } from '@consigli/types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ServiceIdIndexValue } from '@/routes';
import {
  compareWithOfferServiceTypes,
  compareWithTenantServiceTypes,
} from '@/util/translate-service-types';

import { useCompareContext } from '../compare/use-compare-packages';

export const SecondaryService = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const [getPackages] = useLazyGetPackagesByTypeQuery();
  const {
    setSelectedComparisonPackage,
    compareServiceName,
    setCompareServiceName,
    setComparePackages: setPackages,
  } = useCompareContext();

  const comparableInsightServices = useMemo(() => {
    if (serviceName === ServiceName.TENANT_REQUIREMENTS) {
      return compareWithTenantServiceTypes(t);
    } else if (serviceName === ServiceName.OFFER) {
      return compareWithOfferServiceTypes(t);
    }
    return [];
  }, [t, serviceName]);

  const handlePackages = useCallback(
    async (id: DocumentPackageType) => {
      const response = await getPackages({
        projectId,
        type: id,
      }).unwrap();
      return response;
    },
    [getPackages, projectId],
  );

  const handleServiceChange = useCallback(
    async (selectedOptions: SelectOption[]): Promise<void> => {
      if (selectedOptions.length > 0) {
        const option = selectedOptions[0];
        const pkge = comparableInsightServices.find((spec) => spec.value === option.value);
        setCompareServiceName(pkge);
        const type = ServiceIdIndexValue[option.value as ServiceName] as DocumentPackageType;
        const response = await handlePackages(type);
        setPackages(response);
        setSelectedComparisonPackage(undefined);
      } else {
        setCompareServiceName(undefined);
        setSelectedComparisonPackage(undefined);
      }
    },
    [
      comparableInsightServices,
      handlePackages,
      setCompareServiceName,
      setPackages,
      setSelectedComparisonPackage,
    ],
  );
  return (
    <>
      <div className="text-base font-semibold mb-2 text-day-neutral-subtle">
        {t('package-creation.compare-with-service')}
      </div>
      <CustomSelect
        options={comparableInsightServices}
        onChange={handleServiceChange}
        value={
          compareServiceName
            ? [{ label: compareServiceName.label, value: compareServiceName.value }]
            : []
        }
        placeholder={t('package-creation.select-service')}
        isClearable
        noOptionsMessage={t('custom-select.no-options')}
      />
    </>
  );
};
